import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoanProduct } from '../types/responses/LoanProduct';
import AppConfig from '../../../../lib/AppConfig';
import { LoanProductsRepository } from '../repository';
import { LoanProductTemplate } from '../types/responses/Template';
import { EditLoanFormState } from '../types/EditLoan';

interface LoanProductsState {
	allLoanProducts: LoanProduct[];
	displayedLoanProducts: LoanProduct[];
	loanSearchQuery: string;
	loanCurrentPage: number;
	loanPageSize: number;
	loanLoading: boolean;
	loanProductTemplateLoading: boolean;
	loanError: string | null;
	loanProductTemplateError: string | null;
	loanProductTemplate?: LoanProductTemplate;
	editLoanProductState?: EditLoanFormState;
}

const initialState: LoanProductsState = {
	allLoanProducts: [],
	displayedLoanProducts: [],
	loanSearchQuery: '',
	loanCurrentPage: 0,
	loanPageSize: AppConfig.defaultPageSize,
	loanLoading: false,
	loanProductTemplateLoading: false,
	loanError: null,
	loanProductTemplateError: null,
};

export const fetchLoanProducts = createAsyncThunk(
	'loanProducts/fetchLoanProducts',
	async (_, { rejectWithValue }) => {
		try {
			const data = await LoanProductsRepository.listLoanProducts();
			return data.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
		} catch (err: any) {
			return rejectWithValue(err.toString());
		}
	},
);

export const fetchLoanProductTemplate = createAsyncThunk(
	'loanProducts/fetchLoanProductTemplate',
	async (productId: number, { rejectWithValue }) => {
		try {
			const template = await LoanProductsRepository.getLoanProductTemplate(productId);
			return template;
		} catch (err: any) {
			return rejectWithValue(err.toString());
		}
	},
);

const loanProductsSlice = createSlice({
	name: 'loanProducts',
	initialState,
	reducers: {
		setLoanSearchQuery(state, action: PayloadAction<string>) {
			state.loanSearchQuery = action.payload;
		},
		setLoanCurrentPage(state, action: PayloadAction<number>) {
			state.loanCurrentPage = action.payload;
		},
		setLoanPageSize(state, action: PayloadAction<number>) {
			state.loanPageSize = action.payload;
		},
		updateDisplayedLoanProducts(state) {
			const filtered = state.allLoanProducts.filter(
				product =>
					product.name?.toLowerCase().includes(state.loanSearchQuery.toLowerCase()) ||
					product.shortName?.toLowerCase().includes(state.loanSearchQuery.toLowerCase()) ||
					product.description?.toLowerCase().includes(state.loanSearchQuery.toLowerCase()),
			);
			const start = state.loanCurrentPage * state.loanPageSize;
			const end = start + state.loanPageSize;
			state.displayedLoanProducts = filtered.slice(start, end);
		},
		updateEditLoanProductState(state, action: PayloadAction<EditLoanFormState>) {
			state.editLoanProductState = { ...state.editLoanProductState, ...action.payload };
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchLoanProducts.pending, state => {
				state.loanLoading = true;
				state.loanError = null;
			})
			.addCase(fetchLoanProducts.fulfilled, (state, action) => {
				state.allLoanProducts = action.payload;
				state.loanCurrentPage = 0;
				const start = 0;
				const end = start + state.loanPageSize;
				state.displayedLoanProducts = action.payload.slice(start, end);
				state.loanLoading = false;
			})
			.addCase(fetchLoanProducts.rejected, (state, action) => {
				state.loanError = action.payload as string;
				state.loanLoading = false;
			})
			.addCase(fetchLoanProductTemplate.pending, state => {
				state.loanProductTemplateLoading = true;
				state.loanError = null;
			})
			.addCase(fetchLoanProductTemplate.fulfilled, (state, action) => {
				state.loanProductTemplate = action.payload;
				state.loanProductTemplateLoading = false;
			})
			.addCase(fetchLoanProductTemplate.rejected, (state, action) => {
				state.loanProductTemplateError = action.payload as string;
				state.loanProductTemplateLoading = false;
			});
	},
});

export const {
	setLoanSearchQuery,
	setLoanCurrentPage,
	setLoanPageSize,
	updateDisplayedLoanProducts,
	updateEditLoanProductState,
} = loanProductsSlice.actions;

export default loanProductsSlice.reducer;
